/**
 * BudgetFitter Home Stories Frontend Logic
 * Spotlight / Revolut-style cards with lazy video loading, rail navigation,
 * viewed state persistence via localStorage, and ambient-glow modal player.
 */

document.addEventListener('DOMContentLoaded', () => {
  const storyTriggers = document.querySelectorAll('.js-bfwt-story-trigger');
  const modal = document.getElementById('bfwt-story-modal');
  const railContainer = document.querySelector('.bfwt-stories-container');
  const railPrevBtn = document.getElementById('bfwt-rail-prev');
  const railNextBtn = document.getElementById('bfwt-rail-next');

  if (!storyTriggers.length || !modal) {
    return;
  }

  // ─── Viewed Stories Persistence (localStorage) ───
  const VIEWED_KEY = 'bf_viewed_stories';
  const getViewedStories = () => {
    try {
      return JSON.parse(localStorage.getItem(VIEWED_KEY)) || [];
    } catch {
      return [];
    }
  };

  const markStoryViewed = (storyId) => {
    if (!storyId) return;
    try {
      const viewed = getViewedStories();
      if (!viewed.includes(String(storyId))) {
        viewed.push(String(storyId));
        localStorage.setItem(VIEWED_KEY, JSON.stringify(viewed));
      }
    } catch {
      /* storage disabled/full */
    }
  };

  // Mark cards that were previously viewed
  const initialViewed = getViewedStories();
  storyTriggers.forEach((btn) => {
    const id = btn.getAttribute('data-story-id');
    if (id && initialViewed.includes(String(id))) {
      const card = btn.querySelector('.bfwt-story-card');
      if (card) {
        card.classList.add('is-viewed');
      }
    }
  });

  // ─── Rail Scroll Controls (Desktop) ───
  if (railContainer) {
    if (railPrevBtn) {
      railPrevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        railContainer.scrollBy({ left: -380, behavior: 'smooth' });
      });
    }
    if (railNextBtn) {
      railNextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        railContainer.scrollBy({ left: 380, behavior: 'smooth' });
      });
    }
  }

  // Portal the modal to <body> so it escapes stacking contexts
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  const iframeContainer = document.getElementById(
    'bfwt-story-iframe-container'
  );
  const nextBtn = document.getElementById('bfwt-story-next');
  const prevBtn = document.getElementById('bfwt-story-prev');
  const ambientGlow = document.getElementById('bfwt-story-ambient-glow');

  // Store stories logic
  const storiesArray = Array.from(storyTriggers).map((btn) => ({
    url: btn.getAttribute('data-story-url'),
    id: btn.getAttribute('data-story-id'),
    element: btn,
  }));
  let currentIndex = -1;
  let lastTriggerElement = null;

  // ─── Lazy Video Observer & Play-on-Hover ───
  const lazyVideos = document.querySelectorAll('.bfwt-story-card__video');

  if (lazyVideos.length > 0 && 'IntersectionObserver' in window) {
    const isHoverCapable = window.matchMedia('(hover: hover)').matches;
    const MAX_PLAYING = isHoverCapable ? 4 : 2;
    const playingVideos = [];

    const startPlaying = (video) => {
      if (playingVideos.includes(video)) {
        return;
      }
      if (playingVideos.length >= MAX_PLAYING) {
        const oldest = playingVideos.shift();
        oldest.pause();
        oldest.classList.remove('is-playing');
      }
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            video.classList.add('is-playing');
            playingVideos.push(video);
          })
          .catch(() => {
            // Autoplay blocked — poster image stays visible
          });
      }
    };

    const stopPlaying = (video) => {
      video.pause();
      video.classList.remove('is-playing');
      const idx = playingVideos.indexOf(video);
      if (idx !== -1) {
        playingVideos.splice(idx, 1);
      }
    };

    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const cardButton = video.closest('.js-bfwt-story-trigger');
          const index = cardButton
            ? parseInt(cardButton.getAttribute('data-index') || '0', 10)
            : 999;

          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
              video.load();
              video.addEventListener(
                'canplay',
                () => {
                  if (!isHoverCapable || index < 3) {
                    startPlaying(video);
                  }
                },
                { once: true }
              );
              video.addEventListener(
                'error',
                () => {
                  video.classList.remove('bfwt-story-card__video');
                },
                { once: true }
              );
            } else if (video.src) {
              if (!isHoverCapable || index < 3) {
                startPlaying(video);
              }
            }
          } else {
            stopPlaying(video);
          }
        });
      },
      {
        rootMargin: '0px 80px',
        threshold: 0.3,
      }
    );

    lazyVideos.forEach((video) => videoObserver.observe(video));

    // Desktop hover interaction
    if (isHoverCapable) {
      lazyVideos.forEach((video) => {
        const cardButton = video.closest('.js-bfwt-story-trigger');
        if (!cardButton) {
          return;
        }
        const index = parseInt(
          cardButton.getAttribute('data-index') || '0',
          10
        );

        cardButton.addEventListener('mouseenter', () => {
          if (!video.src && video.dataset.src) {
            video.src = video.dataset.src;
            video.load();
            video.addEventListener('canplay', () => startPlaying(video), {
              once: true,
            });
          } else if (video.src) {
            startPlaying(video);
          }
        });

        cardButton.addEventListener('mouseleave', () => {
          if (index >= 3) {
            stopPlaying(video);
          }
        });
      });
    }
  }

  // Only allow http(s) URLs for iframe src
  const safeStoryUrl = (url) => {
    if (!url || typeof url !== 'string') {
      return null;
    }
    try {
      const u = new globalThis.URL(url, window.location.origin);
      if (u.protocol === 'http:' || u.protocol === 'https:') {
        return url;
      }
    } catch {
      /* invalid URL */
    }
    return null;
  };

  const escapeHtmlAttr = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  // Load Story Function
  const loadStory = (index) => {
    if (index < 0 || index >= storiesArray.length) {
      return;
    }
    currentIndex = index;
    const story = storiesArray[currentIndex];

    const baseUrl = safeStoryUrl(story.url);
    if (!baseUrl) {
      return;
    }
    const storySrc = baseUrl.includes('embedMode=1')
      ? baseUrl
      : `${baseUrl.replace(/#.*$/, '')}#embedMode=1`;

    iframeContainer.innerHTML = `<iframe src="${escapeHtmlAttr(storySrc)}" title="Story Viewer" frameborder="0" scrolling="no" allowfullscreen allow="autoplay *; fullscreen *; encrypted-media *"></iframe>`;

    // Mark as viewed in DOM and localStorage
    const card = story.element.querySelector('.bfwt-story-card');
    if (card) {
      card.classList.add('is-viewed');
    }
    markStoryViewed(story.id);

    // Dynamic Ambient Glow pulse
    if (ambientGlow) {
      ambientGlow.style.opacity = '0.4';
      setTimeout(() => {
        ambientGlow.style.opacity = '0.7';
      }, 200);
    }

    if (typeof window.bfwt_trackEvent === 'function') {
      window.bfwt_trackEvent('story_view', {
        story_index: index,
        event_category: 'engagement',
      });
    }

    if (prevBtn) {
      prevBtn.style.visibility = currentIndex > 0 ? 'visible' : 'hidden';
      prevBtn.style.pointerEvents = currentIndex > 0 ? 'auto' : 'none';
      prevBtn.setAttribute('aria-hidden', currentIndex > 0 ? 'false' : 'true');
    }
    if (nextBtn) {
      const hasNext = currentIndex < storiesArray.length - 1;
      nextBtn.style.visibility = hasNext ? 'visible' : 'hidden';
      nextBtn.style.pointerEvents = hasNext ? 'auto' : 'none';
      nextBtn.setAttribute('aria-hidden', hasNext ? 'false' : 'true');
    }

    if (!modal.classList.contains('is-active')) {
      lastTriggerElement = story.element;
      document.body.style.overflow = 'hidden';
      modal.classList.add('is-active');
      modal.setAttribute('aria-hidden', 'false');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
    }
  };

  // Open Modal on Card Click
  storyTriggers.forEach((btn, index) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      loadStory(index);
    });
  });

  // Close Modal Function
  const closeModal = () => {
    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    modal.removeAttribute('role');
    modal.removeAttribute('aria-modal');
    document.body.style.overflow = '';
    currentIndex = -1;

    if (lastTriggerElement && typeof lastTriggerElement.focus === 'function') {
      try {
        lastTriggerElement.focus();
      } catch {
        /* noop */
      }
    }
    lastTriggerElement = null;

    setTimeout(() => {
      iframeContainer.innerHTML = '';
    }, 300);
  };

  // Nav Buttons (Desktop Side Chevrons)
  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      loadStory(currentIndex + 1);
    });
  }
  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      loadStory(currentIndex - 1);
    });
  }

  // Close when clicking the backdrop
  modal.addEventListener('click', (e) => {
    if (e.target === modal || e.target.closest('.bfwt-story-modal-close')) {
      closeModal();
    }
  });

  // Keyboard navigation
  document.addEventListener('keydown', (e) => {
    if (!modal.classList.contains('is-active')) {
      return;
    }
    if (e.key === 'Escape') {
      closeModal();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === ' ') {
      // Forward navigation & pause events to the active story player inside the iframe
      const iframe = iframeContainer.querySelector('iframe');
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: 'story:keyboard',
            key: e.key,
          },
          '*'
        );
      }
    }
  });

  // Handle messages dispatched from the child story player inside the iframe
  window.addEventListener('message', (e) => {
    if (!e.data || typeof e.data !== 'object') return;
    if (e.data.type === 'story:close') {
      closeModal();
    } else if (e.data.type === 'story:navigate' && e.data.url) {
      closeModal();
      window.location.href = e.data.url;
    } else if (e.data.type === 'story:next') {
      if (currentIndex < storiesArray.length - 1) {
        loadStory(currentIndex + 1);
      } else {
        closeModal();
      }
    } else if (e.data.type === 'story:prev') {
      if (currentIndex > 0) {
        loadStory(currentIndex - 1);
      }
    }
  });
});

